import type { Option } from '@openx/types/options';
import type { CriteriaObjectOa, PackageListItem, PackagesListFiltersValue } from '@openx/types/package';
import type { UsePackageUsedByResponse } from '@openx/types/package/PackageHookTypes';
import type { ToggleSelect } from '@openx/types/selectableTable';
import type { ChangeCriteriaHandler, CriteriaDimension } from '@openx/types/tableCriteria';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { type JSX, useMemo } from 'react';
import { useLocation } from 'react-router';

import type { PackageRowActionsType } from '../../types';
import { usePackageContext } from '../../utils/context/PackageContext';
import { PACKAGE_LIST_TABS, usePackageListTabs } from './hooks/usePackageListTabs';
import { usePackagesList } from './hooks/usePackagesList';
import { PackagesTable } from './PackagesTable';
import { PackagesTopBar } from './PackagesTopBar';

export type PackagesListProps = {
  actions: PackageRowActionsType;
  changeFilters: (filters: PackagesListFiltersValue) => void;
  changeSingleCriteriaDimension: ChangeCriteriaHandler<CriteriaDimension, PackagesListFiltersValue>;
  toggleSelect: ToggleSelect<PackageListItem>;
  criteriaObjectOa?: CriteriaObjectOa;
  showCuratedBadge?: boolean;
  selectedPackages: PackageListItem[];
  packageUsedBy: UsePackageUsedByResponse;
  organizationOptions?: Option[];
  isOrganizationLoading?: boolean;
};

export const PackagesList = ({
  actions,
  changeFilters,
  changeSingleCriteriaDimension,
  toggleSelect,
  criteriaObjectOa,
  showCuratedBadge = false,
  selectedPackages,
  packageUsedBy,
  organizationOptions,
  isOrganizationLoading,
}: PackagesListProps): JSX.Element => {
  const { usePackagePermissions } = usePackageContext();
  const { isOa } = usePermissionContext();
  const { isInternalUser } = usePackagePermissions();
  const location = useLocation();
  const { activeTab } = usePackageListTabs(location.pathname, true);
  const isCuratedTab = activeTab === PACKAGE_LIST_TABS.CURATED;

  const hideUsedByColumn = useMemo(
    () => isOa && !isInternalUser && activeTab === PACKAGE_LIST_TABS.CURATED,
    [activeTab, isInternalUser, isOa]
  );

  const hideOrganizationColumn = useMemo(() => !isOa || (isOa && !isInternalUser), [isOa, isInternalUser]);

  const shouldFetchRelatedAccounts = useMemo(
    () => !(hideOrganizationColumn && isCuratedTab),
    [hideOrganizationColumn, isCuratedTab]
  );

  const {
    adPlacementOptions,
    criteria,
    deviceTypeOptions,
    distributionChannelOptions,
    isAdPlacementLoading,
    isDeviceTypeLoading,
    isDistributionChannelLoading,
    loading,
    mappedPackagesList,
    relatedAccounts,
  } = usePackagesList(criteriaObjectOa, {
    fetchRelatedAccounts: shouldFetchRelatedAccounts,
  });

  return (
    <>
      <PackagesTopBar
        adPlacementOptions={adPlacementOptions}
        deviceTypeOptions={deviceTypeOptions}
        distributionChannelOptions={distributionChannelOptions}
        filters={criteria.filters as PackagesListFiltersValue}
        isAdPlacementLoading={isAdPlacementLoading}
        isDeviceTypeLoading={isDeviceTypeLoading}
        isDistributionChannelLoading={isDistributionChannelLoading}
        onChange={changeFilters}
        organizationOptions={organizationOptions}
        isOrganizationLoading={isOrganizationLoading}
      />
      <PackagesTable
        actions={actions}
        criteria={criteria}
        hideActions={hideUsedByColumn}
        hideToggleSelect={hideUsedByColumn}
        hideUsedByColumn={hideUsedByColumn}
        isCuratedList={isCuratedTab}
        loading={loading}
        onCriteriaChange={changeSingleCriteriaDimension}
        packagesAccounts={relatedAccounts}
        packagesList={mappedPackagesList}
        selectedData={selectedPackages}
        showCuratedBadge={showCuratedBadge}
        toggleSelect={toggleSelect}
        usedBy={{ data: packageUsedBy.data, loading: packageUsedBy.isLoading }}
        hideOrganizationColumn={hideOrganizationColumn}
      />
    </>
  );
};
